"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { CirclePlay, Bell, Calendar, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
const roadmapData = [{
  id: 1,
  day: "MON",
  time: "08:00 AM - 04:00 PM",
  title: "Kanata and Nepean",
  episode: "Zone 1",
  status: "released",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1623234/img/ottawa_kanata.png",
  type: "Residential",
  desc: "Our team will be in the West end collecting from all residential addresses in Kanata and Nepean. Leave bags on your porch."
}, {
  id: 2,
  day: "TUE",
  time: "08:00 AM - 04:00 PM",
  title: "Downtown Ottawa",
  episode: "Zone 2",
  status: "upcoming",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1623234/img/ottawa_nepean.png",
  type: "Residential",
  desc: "Mid-week pickups covering the Downtown Ottawa area. Ensure items are visible from the street for our driver."
}, {
  id: 3,
  day: "WED",
  time: "09:00 AM - 05:00 PM",
  title: "Downtown & Centretown",
  episode: "Zone 3",
  status: "upcoming",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1623234/img/ottawa_downtown.png",
  type: "Residential/Condo",
  desc: "Navigating the heart of the city. We collect from porches, lobbies, and designated pickup spots in the urban core."
}, {
  id: 4,
  day: "THU",
  time: "08:00 AM - 04:00 PM",
  title: "Orleans & Gloucester",
  episode: "Zone 4",
  status: "upcoming",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1623234/img/ottawa_orleans.png",
  type: "Residential",
  desc: "Heading East to Orleans. Please label your bags clearly with 'Second Chance Threads' for easy identification and sorting."
}, {
  id: 5,
  day: "FRI",
  time: "09:00 AM - 03:00 PM",
  title: "Glebe & Old Ottawa South",
  episode: "Zone 5",
  status: "upcoming",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1623234/img/ottawa_glebe.png",
  type: "Residential",
  desc: "Final pickups of the week in the Glebe area. Perfect for those end-of-week closet cleanouts before the weekend."
}];
function TimelineItem({
  item,
  index
}: {
  item: typeof roadmapData[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: false,
    threshold: 0.6,
    rootMargin: "-15% 0px -15% 0px"
  });
  const isReleased = item.status === "released";
  return <div data-section-id="123450" data-index={index} ref={ref} className="flex items-start md:items-center gap-4 md:gap-8 relative group w-full">
      {}
      <div className="w-[60px] md:w-[100px] shrink-0 text-right pt-3 md:pt-0">
        <div className={cn("text-xl md:text-3xl font-bold font-default transition-colors duration-500 tracking-tighter", isVisible ? "text-primary drop-shadow-[0_0_8px_var(--color-primary)]" : "text-foreground/40")}>
          {item.day}
        </div>
        <div className="text-[10px] md:text-xs text-muted-foreground font-sans mt-1">
          {item.time}
        </div>
      </div>

      {}
      <div className="w-8 h-8 shrink-0 flex items-center justify-center z-10 mt-3 md:mt-0">
        <div className={cn("w-3 h-3 rounded-full transition-all duration-500", isVisible ? "bg-primary scale-[2] shadow-[0_0_20px_var(--color-primary)]" : isReleased ? "bg-muted-foreground/50 border-2 border-muted" : "bg-background border-2 border-primary/40")} />
      </div>

      {}
      <div className="flex-1 py-2 min-w-0">
        <Card className={cn("overflow-hidden transition-all duration-700 border-border/50 bg-card backdrop-blur-md", isReleased ? "opacity-60 grayscale-[10%] hover:grayscale-0 hover:opacity-100" : "hover:border-primary/40 hover:bg-card/90", isVisible && !isReleased ? "border-primary/50 shadow-[0_0_30px_-10px_var(--color-primary)] bg-card translate-x-2 md:translate-x-4" : "")}>
          <div className="flex flex-col sm:flex-row h-full">
            {}
            <div className="sm:w-48 h-32 sm:h-auto shrink-0 relative overflow-hidden">
              <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent sm:bg-gradient-to-r sm:from-transparent sm:via-background/50 sm:to-background" />
            </div>

            {}
            <div className="p-4 md:p-6 flex flex-col justify-center flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <Badge variant="outline" className="font-sans text-[10px] uppercase tracking-wider border-primary/30 text-primary bg-primary/5">
                      {item.episode}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground font-sans uppercase tracking-widest">
                      {item.type}
                    </span>
                  </div>
                  <h3 className="text-lg md:text-xl font-bold font-default leading-tight truncate">
                    {item.title}
                  </h3>
                </div>
                {isReleased && <Badge className="bg-primary/10 text-primary border-primary/30 animate-pulse whitespace-nowrap shrink-0">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Active Today
                  </Badge>}
              </div>
              
              <p className="text-sm md:text-base text-muted-foreground font-sans leading-relaxed mb-6 max-w-md">
                {item.desc}
              </p>
              
              <div className="flex items-center gap-3 mt-auto pt-4">
                {isReleased ? <Button size="lg" variant="default" asChild className="w-full sm:w-auto">
                    <Link to="#">
                      <CirclePlay className="w-4 h-4 mr-2" /> Schedule a Free Pickup
                    </Link>
                  </Button> : <Button size="lg" variant="outline" asChild className="w-full sm:w-auto border-primary/30 text-primary hover:bg-primary/10 transition-colors">
                    <Link to="#">
                      <Bell className="w-4 h-4 mr-2" /> Area Details
                    </Link>
                  </Button>}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>;
}
export default function ReleaseRoadmap() {
  return <section id="weekly-pickup-calendar" className="bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 -left-64 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] opacity-50" />
        <div className="absolute bottom-1/4 -right-64 w-[500px] h-[500px] bg-secondary/5 rounded-full blur-[120px] opacity-50" />
        
        {}
        <div className="absolute top-40 right-10 text-[120px] font-black text-foreground/[0.03] select-none rotate-90 origin-right tracking-[0.2em] font-default">
          SCHEDULE
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary bg-primary/5">
            <SafeHydrate><Calendar className="w-3 h-3 mr-2" /></SafeHydrate>
            Ottawa Pickup Schedule
          </Badge>
          <h2 className="text-3xl md:text-5xl font-bold font-default mb-4 tracking-tight">
            Weekly Pickup <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Calendar</span>
          </h2>
          <p className="text-muted-foreground font-sans max-w-2xl mx-auto text-lg">
            We visit different neighborhoods across the city every day. Find your zone below and book your free no-contact clothing collection.
          </p>
        </div>

        <div className="relative">
          {}
          <div className="absolute left-[92px] md:left-[148px] top-4 bottom-4 w-px bg-gradient-to-b from-transparent via-primary/30 to-transparent" />

          <div className="flex flex-col gap-16 md:gap-24">
            {roadmapData.map((item, index) => <TimelineItem key={item.id} item={item} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}